import { useState, useEffect } from "react";

export interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const MILLISECONDS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;

const calculateTimeUnits = (difference: number): TimeLeft => ({
  days: Math.floor(
    difference /
      (MILLISECONDS_PER_SECOND *
        SECONDS_PER_MINUTE *
        MINUTES_PER_HOUR *
        HOURS_PER_DAY)
  ),
  hours: Math.floor(
    (difference /
      (MILLISECONDS_PER_SECOND * SECONDS_PER_MINUTE * MINUTES_PER_HOUR)) %
      HOURS_PER_DAY
  ),
  minutes: Math.floor(
    (difference / (MILLISECONDS_PER_SECOND * SECONDS_PER_MINUTE)) %
      SECONDS_PER_MINUTE
  ),
  seconds: Math.floor(
    (difference / MILLISECONDS_PER_SECOND) % SECONDS_PER_MINUTE
  ),
});

const getTimeDifference = (targetDate: Date): number => {
  return targetDate.getTime() - new Date().getTime();
};

const isValidDate = (date: Date): boolean => {
  return !isNaN(date.getTime());
};

const calculateTimeLeft = (dateTime?: string): TimeLeft | null => {
  if (!dateTime) {
    return null;
  }

  const targetDate = new Date(dateTime);
  if (!isValidDate(targetDate)) {
    return null;
  }

  const difference = getTimeDifference(targetDate);
  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  return calculateTimeUnits(difference);
};

export const useCountdownTimer = (dateTime?: string) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);

  useEffect(() => {
    const updateTimer = () => {
      const newTimeLeft = calculateTimeLeft(dateTime);
      setTimeLeft(newTimeLeft);
      return newTimeLeft;
    };

    const initialTimeLeft = updateTimer();
    if (initialTimeLeft === null) return;

    const timer = setInterval(() => {
      const newTimeLeft = updateTimer();
      const isTimerComplete =
        newTimeLeft === null ||
        Object.values(newTimeLeft).every((value) => value === 0);

      if (isTimerComplete) {
        clearInterval(timer);
      }
    }, MILLISECONDS_PER_SECOND);

    return () => clearInterval(timer);
  }, [dateTime]);

  return timeLeft;
};
